import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import FilterCollapse from "components/filter_form/filter_collapse/FilterCollapse";
import FilterCollapseHeader from "components/filter_form/filter_collapse_header/FilterCollapseHeader";
import FilterFormTitle from "components/filter_form/filter_form_title/FilterFormTitle";
import { countFilters } from "components/filter_form/utils";
import { SelectBuyerList } from "components/form_components/BuyerListSelect";
import { BuyerSelect } from "components/form_components/BuyerSelect";
import { BuyerTypeSelect } from "components/form_components/BuyerTypeSelect";
import { FrameworkSelect } from "components/form_components/FrameworkSelect";
import { DateRange } from "components/form_components/Inputs";
import NoticeStageSelect from "components/form_components/NoticeStageSelect";
import { SupplierSelect } from "components/form_components/SupplierSelect";
import Checkbox from "components/ui/checkbox/Checkbox";
import InputNumber from "components/ui/input/InputNumber";
import { useFormAutoSubmit } from "lib/hooks/useFormAutoSubmit";
import { NoticesStagesv2 } from "lib/types/graphQLEnums";
import { camelToSentence } from "lib/utils";
import { DEFAULT_FILTERS, getAppliedFilters, NoticeV2Filters } from "./utils";

const groupedKeys = {
  procurementStage: ["stage"],
  buyers: ["buyerIds", "buyerListIds", "buyerCategories"],
  suppliers: ["supplierIds"],
  frameworks: ["frameworkIds"],
  contractDates: ["publishDate", "expiryDate"],
  contractValue: ["value"],
} satisfies Record<string, (keyof NoticeV2Filters)[]>;

type GroupKey = keyof typeof groupedKeys;

type FilterFormProps = {
  onChange: (filters: NoticeV2Filters) => void;
  filters: NoticeV2Filters;
  onClose: () => void;
  activeGroups: string[];
  onActiveGroupsChange: (value: string[]) => void;
  isSidebar?: boolean;
  isEmptyState?: boolean;
  /** When true, filter controls are disabled until the user has applied a topic search. */
  filtersDisabled?: boolean;
  onSideBarOpenChange?: (open: boolean) => void;
  hideSidebarCollapseButton?: boolean;
};

export function FilterForm({
  onChange,
  filters,
  onClose,
  activeGroups,
  onActiveGroupsChange,
  isSidebar,
  isEmptyState,
  filtersDisabled = false,
  onSideBarOpenChange,
  hideSidebarCollapseButton = false,
}: FilterFormProps) {
  const { control, watch, trigger, getValues, reset, setValue } = useForm<NoticeV2Filters>({
    mode: "onChange",
    defaultValues: filters,
  });

  const prevFiltersDisabled = useRef(filtersDisabled);
  useEffect(() => {
    if (prevFiltersDisabled.current && !filtersDisabled) reset(filters);
    prevFiltersDisabled.current = filtersDisabled;
  }, [filtersDisabled, filters, reset]);

  useFormAutoSubmit({
    watch,
    trigger,
    getValues,
    onChange: (formValues) => {
      const publishDate = { ...formValues.publishDate };

      const from = publishDate?.from as string | Date | undefined;
      if (from instanceof Date) {
        publishDate.from = from.toISOString();
      }

      const to = publishDate?.to as string | Date | undefined;
      if (to instanceof Date) {
        publishDate.to = to.toISOString();
      }

      const expiryDate = { ...formValues.expiryDate };

      const expiryFrom = expiryDate?.from as string | Date | undefined;
      if (expiryFrom instanceof Date) {
        expiryDate.from = expiryFrom.toISOString();
      }

      const expiryTo = expiryDate?.to as string | Date | undefined;
      if (expiryTo instanceof Date) {
        expiryDate.to = expiryTo.toISOString();
      }

      onChange({
        ...filters,
        ...formValues,
        publishDate,
        expiryDate,
        topicSearch: filters.topicSearch,
      });
    },
  });

  const counts = countFilters(filters, groupedKeys);

  function clearFilter(groupKey: GroupKey) {
    const keys = groupedKeys[groupKey];
    if (!keys) return;
    keys.forEach((k) => {
      setValue(k, DEFAULT_FILTERS[k]);
    });
    onChange({
      ...filters,
      ...Object.fromEntries(keys.map((k) => [k, DEFAULT_FILTERS[k]])),
      topicSearch: filters.topicSearch,
    });
  }

  const items: { key: GroupKey; header: React.ReactNode; content: React.ReactNode }[] = [
    {
      key: "procurementStage",
      header: (
        <FilterCollapseHeader
          title={camelToSentence("procurementStage")}
          count={counts.procurementStage ?? 0}
          clear={() => clearFilter("procurementStage")}
        />
      ),
      content: (
        <FilterContent key="procurementStage-content">
          <Controller
            name="stage"
            control={control}
            render={({ field }) => (
              <div aria-label="Procurement stage filters">
                <NoticeStageSelect
                  {...field}
                  checkedStages={(field.value ?? []) as NoticesStagesv2[]}
                />
              </div>
            )}
          />
        </FilterContent>
      ),
    },
    {
      key: "buyers",
      header: (
        <FilterCollapseHeader
          title="Buyers"
          count={counts.buyers ?? 0}
          clear={() => clearFilter("buyers")}
        />
      ),
      content: (
        <FilterContent key="buyers-content">
          <div aria-label="Buyer filters">
            <BuyerSelect
              name="buyerIds"
              control={control}
              label="Buyers"
              placeholder="Search buyers"
              mode="multiple"
              disabled={(watch("buyerListIds")?.length ?? 0) > 0}
              allowClear
            />
            <SelectBuyerList
              name="buyerListIds"
              label="Buyer lists"
              control={control}
              placeholder="Select..."
              mode="multiple"
              disabled={(watch("buyerIds")?.length ?? 0) > 0}
              allowClear
            />
            <BuyerTypeSelect
              name="buyerCategories"
              control={control}
              multiple
              label="Buyer type"
              placeholder="Select"
              allowClear
            />
          </div>
        </FilterContent>
      ),
    },
    {
      key: "suppliers",
      header: (
        <FilterCollapseHeader
          title="Suppliers"
          count={counts.suppliers ?? 0}
          clear={() => clearFilter("suppliers")}
        />
      ),
      content: (
        <FilterContent key="suppliers-content">
          <div aria-label="Supplier filters">
            <SupplierSelect
              name="supplierIds"
              label="Suppliers"
              control={control}
              allowAccessForAllUsers={true}
              hideSaveAsSignals
            />
          </div>
        </FilterContent>
      ),
    },
    {
      key: "frameworks",
      header: (
        <FilterCollapseHeader
          title="Frameworks"
          count={counts.frameworks ?? 0}
          clear={() => clearFilter("frameworks")}
        />
      ),
      content: (
        <FilterContent key="frameworks-content">
          <div aria-label="Framework filters">
            <FrameworkSelect
              name="frameworkIds"
              control={control}
              label=""
              placeholder="Search frameworks..."
              mode="multiple"
              allowClear
            />
          </div>
        </FilterContent>
      ),
    },
    {
      key: "contractDates",
      header: (
        <FilterCollapseHeader
          title="Contract dates"
          count={counts.contractDates ?? 0}
          clear={() => clearFilter("contractDates")}
        />
      ),
      content: (
        <FilterContent key="contractDates-content">
          <Flex column gap={12} aria-label="Contract date filters">
            <Flex column gap={8}>
              <Text fontWeight="500">Publish date</Text>
              {/* <div aria-label="Contract date filters">
            <DateInput name="publishDate" control={control} calendarProps={{ mode: "range" }} />
          </div> */}
              <DateRange name="publishDate" control={control} label="Published date" />
            </Flex>
            <Flex column gap={8}>
              <Text fontWeight="500">Expiry date</Text>
              {/* <DateInput name="expiryDate" control={control} calendarProps={{ mode: "range" }} /> */}
              <DateRange name="expiryDate" control={control} label="Expiry date" />
              <Controller
                name="expiryDate.hideNulls"
                control={control}
                render={({ field }) => (
                  <Flex alignItems="center" gap={8} style={{ cursor: "pointer" }} as="label">
                    <Checkbox
                      checked={field.value === true}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                    <Text>Hide notices without expiry dates</Text>
                  </Flex>
                )}
              />
            </Flex>
          </Flex>
        </FilterContent>
      ),
    },
    {
      key: "contractValue",
      header: (
        <FilterCollapseHeader
          title="Contract value"
          count={counts.contractValue ?? 0}
          clear={() => clearFilter("contractValue")}
        />
      ),
      content: (
        <FilterContent key="contractValue-content">
          <Flex column gap={8} aria-label="Value filters">
            <Flex alignItems="center" gap={8}>
              <Controller
                name="value.from"
                control={control}
                rules={{ min: 0 }}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    name="value.from"
                    onChange={(e) => {
                      const n = e.target.valueAsNumber;
                      field.onChange(Number.isFinite(n) ? n : undefined);
                    }}
                    placeholder="From"
                    min={0}
                  />
                )}
              />
              <Text>–</Text>
              <Controller
                name="value.to"
                control={control}
                rules={{ min: watch("value.from") || 0 }}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    name="value.to"
                    onChange={(e) => {
                      const n = e.target.valueAsNumber;
                      field.onChange(Number.isFinite(n) ? n : undefined);
                    }}
                    placeholder="To"
                    min={watch("value.from") || 0}
                  />
                )}
              />
            </Flex>
            <Controller
              name="value.hideNulls"
              control={control}
              render={({ field }) => (
                <Flex alignItems="center" gap={8} style={{ cursor: "pointer" }} as="label">
                  <Checkbox
                    checked={field.value === true}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                  />
                  <Text>Hide notices without a value</Text>
                </Flex>
              )}
            />
          </Flex>
        </FilterContent>
      ),
    },
  ];

  function clearAll() {
    const preserved = { topicSearch: filters.topicSearch };
    reset({ ...DEFAULT_FILTERS, ...preserved });
    onChange({ ...DEFAULT_FILTERS, ...preserved });
  }

  return (
    <Form aria-label="Notice filters" onSubmit={(e) => e.preventDefault()}>
      <FilterFormTitle
        onClose={onClose}
        isSidebar={isSidebar}
        isEmptyState={isEmptyState}
        showClear={!filtersDisabled && Object.values(counts).some(Boolean)}
        clearFilters={clearAll}
        filtersCount={getAppliedFilters(filters).length}
        onSideBarOpenChange={onSideBarOpenChange}
        hideSidebarCollapseButton={hideSidebarCollapseButton}
      />
      {filtersDisabled ? (
        <FiltersDisabledMessage>Enter a search above to filter results.</FiltersDisabledMessage>
      ) : (
        <FilterCollapse items={items} value={activeGroups} onChange={onActiveGroupsChange} />
      )}
    </Form>
  );
}

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const FilterContent = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  padding: "24px",
  paddingTop: "2px",
});

const FiltersDisabledMessage = styled.div(({ theme }) => ({
  padding: 24,
  color: theme.colors.sysTextSecondary,
  fontSize: 14,
  textAlign: "center",
}));
